import {
  CircleCheck,
  Compass,
  Lightbulb,
  Link2,
  Newspaper,
  Sparkles,
  Target,
} from "lucide-react";
import {
  useCreate,
  useGetList,
  useGetOne,
  useLocaleState,
  useNotify,
  useTranslate,
} from "ra-core";
import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

import {
  Chip,
  DateStepper,
  EmptyState,
  ExternalAnchor,
  LoadingCards,
  PriorityBadge,
  SectionLabel,
} from "./components";
import {
  formatCompactDate,
  formatLongDate,
  isIsoDay,
  latestDate,
  nextDate,
  previousDate,
} from "./dates";
import { MiniMarkdown } from "./MiniMarkdown";
import { riseStyle, hostOf } from "./util";
import { ideaPath } from "./routes";
import type { MarketDailyItem, MarketDailyReport, MarketIdea } from "./types";

const ALL = { page: 1, perPage: 1000 } as const;

/** One report per date, navigated by the dates that have a report. */
export const DailyView = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [locale = "es"] = useLocaleState();
  const [params, setParams] = useSearchParams();
  const dateParam = params.get("fecha");
  const itemParam = params.get("noticia");

  const {
    data: reports,
    isPending,
    error,
  } = useGetList<MarketDailyReport>("market_daily_reports", {
    pagination: ALL,
    sort: { field: "report_date", order: "DESC" },
  });
  const { data: targetItem } = useGetOne<MarketDailyItem>(
    "market_daily_items",
    { id: itemParam ?? "" },
    { enabled: !!itemParam && !dateParam },
  );

  const dates = useMemo(
    () => (reports ?? []).map((report) => report.report_date),
    [reports],
  );
  const latest = latestDate(dates);
  const itemDate = targetItem
    ? reports?.find(
        (report) => String(report.id) === String(targetItem.report_id),
      )?.report_date
    : undefined;
  const selected = isIsoDay(dateParam) ? dateParam : (itemDate ?? latest);
  const report = reports?.find((entry) => entry.report_date === selected);

  const go = (date: string | undefined) => {
    if (date) setParams({ fecha: date });
  };

  if (isPending) return <LoadingCards />;
  if (error)
    return <EmptyState icon={Newspaper} title={translate("market.error")} />;
  if (!latest || !selected)
    return (
      <EmptyState
        icon={Newspaper}
        title={translate("market.daily.no_reports")}
        hint={translate("market.daily.no_reports_hint")}
      />
    );

  const older = previousDate(dates, selected);
  const newer = nextDate(dates, selected);

  return (
    <section
      aria-labelledby="mi-daily-title"
      className="space-y-3 md:space-y-4"
    >
      <div className="space-y-3">
        <DateStepper
          previousLabel={translate("market.nav.previous_report")}
          nextLabel={translate("market.nav.next_report")}
          onPrevious={older ? () => go(older) : undefined}
          onNext={newer ? () => go(newer) : undefined}
          onLatest={selected !== latest ? () => go(latest) : undefined}
          pickerValue={selected}
          onPick={go}
          heading={
            isMobile
              ? {
                  id: "mi-daily-title",
                  text: formatCompactDate(selected, locale),
                  label: formatLongDate(selected, locale),
                }
              : undefined
          }
        />
        {isMobile ? null : (
          <div>
            <p className="text-[11px] font-semibold tracking-[0.06em] text-muted-foreground uppercase">
              {translate("market.daily.heading")}
            </p>
            <h2
              id="mi-daily-title"
              className="text-xl font-semibold tracking-tight wrap-anywhere first-letter:uppercase md:text-[1.375rem]"
            >
              {formatLongDate(selected, locale)}
            </h2>
          </div>
        )}
      </div>
      {report ? (
        <DailyReport report={report} highlightId={itemParam} />
      ) : (
        <EmptyState
          icon={Newspaper}
          title={translate("market.daily.no_report_for", {
            date: formatLongDate(selected, locale),
          })}
          hint={translate("market.daily.no_report_hint")}
          action={
            <Button
              type="button"
              className="btn-save rounded-full px-5"
              onClick={() => go(latest)}
            >
              {translate("market.daily.go_latest")}
            </Button>
          }
        />
      )}
    </section>
  );
};

const DailyReport = ({
  report,
  highlightId,
}: {
  report: MarketDailyReport;
  highlightId: string | null;
}) => {
  const translate = useTranslate();
  const { data: items, isPending } = useGetList<MarketDailyItem>(
    "market_daily_items",
    {
      pagination: ALL,
      sort: { field: "position", order: "ASC" },
      filter: { report_id: report.id },
    },
  );
  // Small table: the whole board shares this cache with the Ideas view.
  const { data: ideas } = useGetList<MarketIdea>("market_ideas", {
    pagination: ALL,
    sort: { field: "updated_at", order: "DESC" },
  });
  // The idea each item was saved as (newest first, so the latest one wins).
  const ideaByItem = useMemo(() => {
    const byItem = new Map<string, MarketIdea>();
    for (const idea of ideas ?? [])
      if (
        idea.source_item_id != null &&
        !byItem.has(String(idea.source_item_id))
      )
        byItem.set(String(idea.source_item_id), idea);
    return byItem;
  }, [ideas]);

  useEffect(() => {
    if (!highlightId || !items?.length) return;
    document
      .getElementById(`mi-item-${highlightId}`)
      ?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [highlightId, items]);

  if (isPending) return <LoadingCards />;

  const sorted = [...(items ?? [])].sort(
    (a, b) => a.position - b.position || Number(a.id) - Number(b.id),
  );
  const hasOpportunity = !!(
    report.opportunity_title || report.opportunity_body
  );

  return (
    <>
      <p className="text-sm text-muted-foreground">
        {translate("market.daily.items", { smart_count: sorted.length })}
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
        {sorted.map((item, index) => (
          <DailyItemCard
            key={item.id}
            item={item}
            index={index}
            highlighted={String(item.id) === highlightId}
            savedIdea={ideaByItem.get(String(item.id))}
          />
        ))}
        {hasOpportunity ? (
          <article
            className="mi-opportunity mi-rise min-w-0 p-5 md:col-span-2 md:p-6"
            style={riseStyle(sorted.length)}
            aria-labelledby="mi-opportunity-title"
          >
            <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.08em] text-white/70 uppercase">
              <Sparkles aria-hidden className="size-3.5" />
              {translate("market.daily.opportunity")}
            </p>
            {report.opportunity_title ? (
              <h3
                id="mi-opportunity-title"
                className="text-lg leading-snug font-semibold wrap-anywhere md:text-xl"
              >
                {report.opportunity_title}
              </h3>
            ) : null}
            <MiniMarkdown
              source={report.opportunity_body}
              className="mt-2 text-white/90"
            />
          </article>
        ) : null}
      </div>
    </>
  );
};

const DailyItemCard = ({
  item,
  index,
  highlighted,
  savedIdea,
}: {
  item: MarketDailyItem;
  index: number;
  highlighted: boolean;
  savedIdea?: MarketIdea;
}) => {
  const translate = useTranslate();
  const sources = Array.isArray(item.sources) ? item.sources : [];
  const titleId = `mi-item-title-${item.id}`;

  return (
    <article
      id={`mi-item-${item.id}`}
      aria-labelledby={titleId}
      data-highlight={highlighted || undefined}
      className="mi-card mi-rise flex min-w-0 flex-col gap-3 p-4 md:p-5"
      style={riseStyle(index)}
    >
      <div className="mi-chip-row">
        <PriorityBadge priority={item.priority} />
        {item.sector ? <Chip>{item.sector}</Chip> : null}
        {item.product_type ? <Chip>{item.product_type}</Chip> : null}
      </div>
      <h3
        id={titleId}
        className="text-[17px] leading-snug font-semibold wrap-anywhere"
      >
        {item.title}
      </h3>
      {item.what_happens ? (
        <div>
          <SectionLabel icon={Newspaper}>
            {translate("market.daily.what_happens")}
          </SectionLabel>
          <MiniMarkdown source={item.what_happens} />
        </div>
      ) : null}
      {item.why_it_matters ? (
        <div>
          <SectionLabel icon={Target}>
            {translate("market.daily.why_it_matters")}
          </SectionLabel>
          <MiniMarkdown source={item.why_it_matters} />
        </div>
      ) : null}
      {item.idea ? (
        <div className="rounded-2xl bg-primary/[0.07] px-3.5 py-3 dark:bg-primary/12">
          <SectionLabel icon={Compass}>
            {translate("market.daily.idea")}
          </SectionLabel>
          <MiniMarkdown source={item.idea} />
        </div>
      ) : null}
      <div className="mt-auto flex flex-wrap items-end justify-between gap-2 pt-1">
        {sources.length ? (
          <div className="min-w-0 flex-1">
            <SectionLabel icon={Link2}>
              {translate("market.daily.sources")}
            </SectionLabel>
            <ul className="flex flex-col gap-1 text-sm">
              {sources.map((source, sourceIndex) => (
                <li key={sourceIndex} className="min-w-0">
                  <ExternalAnchor href={source.url} className="max-w-full">
                    {source.title || (source.url ? hostOf(source.url) : "")}
                  </ExternalAnchor>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <span />
        )}
        {savedIdea ? (
          <SavedIdeaBadge idea={savedIdea} />
        ) : (
          <SaveAsIdea item={item} />
        )}
      </div>
    </article>
  );
};

/**
 * Status, not an action: the item is already on the ideas board. A quiet
 * green badge (no button chrome) that links to that idea on the Ideas tab.
 */
const SavedIdeaBadge = ({ idea }: { idea: MarketIdea }) => {
  const translate = useTranslate();
  return (
    <Link
      to={ideaPath(idea)}
      className="inline-flex h-7 items-center gap-1.5 rounded-full bg-emerald-500/12 px-2.5 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-500/25 ring-inset underline-offset-2 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-emerald-400/15 dark:text-emerald-200 dark:ring-emerald-400/30"
    >
      <CircleCheck aria-hidden className="size-3.5 shrink-0" />
      {translate("market.daily.idea_exists")}
      <span className="sr-only">: {translate("market.daily.view_idea")}</span>
    </Link>
  );
};

const SaveAsIdea = ({ item }: { item: MarketDailyItem }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [create, { isPending }] = useCreate<MarketIdea>();

  const save = () =>
    create(
      "market_ideas",
      {
        data: {
          title: item.title,
          description: item.idea ?? item.why_it_matters ?? null,
          status: "nueva",
          priority: item.priority,
          sector: item.sector ?? null,
          product_type: item.product_type ?? null,
          source_item_id: item.id,
        },
      },
      {
        onSuccess: () => notify("market.daily.idea_saved", { type: "success" }),
        onError: () => notify("market.ideas.error", { type: "error" }),
      },
    );

  return (
    <Button
      type="button"
      variant="ghost"
      disabled={isPending}
      onClick={save}
      className="h-9 gap-1.5 rounded-full bg-muted px-3 text-sm font-medium hover:bg-muted/70"
    >
      <Lightbulb aria-hidden className="size-4" />
      {translate("market.daily.save_idea")}
    </Button>
  );
};
